import React, { useEffect, useMemo } from "react";
import { brandPalette } from "../../../../constants/brandPalette";
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  SimpleGrid,
  Stack,
  Text,
  VStack,
} from "@chakra-ui/react";
import { useDispatch, useSelector } from "react-redux";
import { useForm } from "react-hook-form";
import Select from "react-select";
import { closeCreateLectureModel } from "../../../../modules/admin-modules/models/Actions";
import MainModel from "../../../../components/Models/main-model";
import MainInput from "../../../../components/Form/input";
import {
  adminModalBodyProps,
  adminModalContentProps,
  adminModalHeaderProps,
  adminSelectStyles,
  withAdminSelectMenuPortal,
} from "../../../../components/Models/admin-modal-theme";
import { getAllGroupsRequest } from "../../../../modules/admin-modules/groups/Actions";
import { getAllSubjectsRequest } from "../../../../modules/admin-modules/subjects/Actions";
import {
  createLectureRequest,
  editLectureRequest,
} from "../../../../modules/admin-modules/lectures/Actions";

const selectPortalProps = {
  menuPortalTarget: typeof document !== "undefined" ? document.body : null,
  menuPosition: "fixed",
  maxMenuHeight: 240,
};

const AddLecture = ({ isAdd, data, setSelectedData }) => {
  const openLecture = useSelector((state) => state.model.isOpen12);
  const groups = useSelector((state) => state.groups.groups);
  const subjects = useSelector((state) => state.subjects.subjects);
  const isLoading = useSelector((state) => state.lectures.isLoading);
  const user = useSelector((state) => state.user.userData);
  const isSuperAdmin = user?.role && user.role[0]?.name === "Super Admin";

  const groupsOptions = groups?.map((g) => ({ value: g.id, label: g.name }));
  const subjectsOptions = subjects?.map((s) => ({ value: s.id, label: s.name }));
  const selectStyles = useMemo(() => withAdminSelectMenuPortal(adminSelectStyles()), []);

  const dispatch = useDispatch();

  useEffect(() => {
    if (openLecture) {
      dispatch(getAllGroupsRequest());
      dispatch(getAllSubjectsRequest());
    }
  }, [openLecture, dispatch]);

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
    reset,
  } = useForm({
    defaultValues: data && Object.keys(data).length ? data : {},
    shouldUnregister: true,
  });

  const subjectDefault =
    data?.subject_id != null
      ? subjectsOptions?.find((o) => o.value === data.subject_id)
      : data?.subject?.id != null
        ? { value: data.subject.id, label: data.subject.name }
        : undefined;

  const groupsDefault =
    data?.groups?.map((val) => {
      if (typeof val === "object" && val.name && val.id) {
        return { label: val.name, value: val.id };
      }
      if (typeof val === "object" && val.label && val.value) {
        return { label: val.label, value: val.value };
      }
      const group = groups?.find((g) => g.id === val);
      return group ? { label: group.name, value: group.id } : { label: "Unknown", value: val };
    }) ?? undefined;

  const header = (
    <Box>
      <Heading
        fontSize="28px"
        fontWeight="800"
        color="var(--text-primary)"
        fontFamily="'Amiri','Tajawal',sans-serif"
        textAlign="center"
      >
        {isAdd ? "Add center" : "Edit center"}
      </Heading>
      <Text
        fontSize="xs"
        mt={1}
        color="var(--text-muted)"
        textTransform="uppercase"
        letterSpacing="0.12em"
        fontWeight="700"
        textAlign="center"
      >
        Center name and linked groups
      </Text>
    </Box>
  );

  const onSubmit = async (values) => {
    const { subject, edit, date, attendance_count, ...filteredValues } = values;
    let formData = new FormData();

    for (const key in filteredValues) {
      if (key === "groups") {
        if (Array.isArray(filteredValues[key])) {
          filteredValues[key].forEach((val, idx) => {
            const groupValue = typeof val === "object" && val.id ? val.id : val;
            formData.append(`groups[${idx}]`, groupValue);
          });
        }
      } else {
        formData.append(key, filteredValues[key]);
      }
    }

    const action = () => {
      dispatch(closeCreateLectureModel());
      setSelectedData({});
      reset();
      if (isAdd) {
        window.location.reload();
      }
    };

    if (isAdd) {
      await dispatch(createLectureRequest(formData, action));
    } else {
      const totel = { ...data, ...filteredValues };
      formData = new FormData();
      for (const key in totel) {
        if (key === "groups") {
          if (Array.isArray(totel[key])) {
            totel[key].forEach((val, idx) => {
              const groupValue = typeof val === "object" && val.id ? val.id : val;
              formData.append(`groups[${idx}]`, groupValue);
            });
          }
        } else {
          formData.append(key, totel[key]);
        }
      }
      formData.append("_method", "PUT");
      dispatch(
        editLectureRequest({
          id: data?.id,
          formData,
          action,
        })
      );
    }
  };

  const closeFunc = () => {
    dispatch(closeCreateLectureModel());
    setSelectedData({});
    reset();
  };

  return (
    <MainModel
      isLoading={isLoading}
      header={header}
      closeFunc={closeFunc}
      openVar={openLecture}
      buttonTitle={null}
      buttonFunc={handleSubmit(onSubmit)}
      size="xl"
      hideDefaultFooter
      contentProps={adminModalContentProps}
      headerProps={adminModalHeaderProps}
      bodyProps={adminModalBodyProps}
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack spacing={6} dir="ltr" w="100%">
          <SimpleGrid columns={{ base: 1, md: 2 }} spacingX={8} spacingY={6} alignItems="start">
            <Box gridColumn={{ base: "auto", md: "1 / -1" }}>
              <MainInput
                label="Center name"
                defaultValues={data?.title}
                error={errors?.title?.message}
                placeholder="Enter center name"
                register={register}
                name="title"
              />
            </Box>

            {isSuperAdmin && (
              <Box gridColumn={{ base: "auto", md: "1 / -1" }}>
                <FormControl>
                  <VStack align="stretch" spacing={2}>
                    <FormLabel m={0} fontSize="14px" fontWeight="700" color="var(--text-primary)" textAlign="left">
                      Subject
                    </FormLabel>
                    <Select
                      inputId="lecture_subject_id"
                      options={subjectsOptions}
                      defaultValue={subjectDefault}
                      onChange={(opt) => setValue("subject_id", opt?.value)}
                      styles={selectStyles}
                      placeholder="Select subject"
                      {...selectPortalProps}
                    />
                  </VStack>
                </FormControl>
              </Box>
            )}

            <Box gridColumn={{ base: "auto", md: "1 / -1" }}>
              <FormControl>
                <VStack align="stretch" spacing={2}>
                    <FormLabel m={0} fontSize="14px" fontWeight="700" color="var(--text-primary)" textAlign="left">
                      Groups
                    </FormLabel>
                  <Select
                    inputId="lecture_groups"
                    isMulti
                    options={groupsOptions}
                    defaultValue={groupsDefault}
                    onChange={(opts) => {
                      const selectedGroups = (opts || []).map((o) => o.value);
                      setValue("groups", selectedGroups);
                    }}
                    styles={selectStyles}
                    placeholder="Select groups"
                    {...selectPortalProps}
                  />
                </VStack>
              </FormControl>
            </Box>
          </SimpleGrid>

          <Flex justify="flex-end" gap={6} pt={2}>
            <Button
              variant="ghost"
              color="var(--text-primary)"
              fontWeight="800"
              _hover={{ textDecoration: "underline", bg: "rgba(91, 13, 22, 0.08)" }}
              onClick={closeFunc}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              bg={brandPalette.primary}
              color="white"
              borderRadius="lg"
              px={8}
              py={6}
              fontWeight="800"
              isLoading={isLoading}
              _hover={{ bg: brandPalette.accentDark }}
              boxShadow="0 10px 24px rgba(91, 13, 22, 0.18)"
            >
              Save changes
            </Button>
          </Flex>
        </Stack>
      </form>
    </MainModel>
  );
};

export default AddLecture;
